import React, { useState, useRef, useCallback } from 'react';
import { TextInput } from '../Controls/Input/TextInput';
import { GlyphButton } from '../Controls/Button/GlyphButton';
import ImageLoader from '../Controls/Loaders/ImageLoader';
import { ReactComponent as ImageLibraryLoad } from '../../public/images/svg/ImageLibraryLoad.svg';
import { ReactComponent as ImageDeviceLoad } from '../../public/images/svg/ImageDeviceLoad.svg';
import { SkitterBookImageCropper } from './SkitterBookImageCropper';
import { SkitterBookFrameSelection } from './SkitterBookFrameSelection';
import { SkitterBookTitlePage } from '../Models/SkitterBookTitlePage';
import ModalButton from '../Controls/Button/ModalButton';
import './css/SkitterBookCoverPage.css';

interface SkitterBookCoverPageProps {
    isEnabled: boolean;
    data: SkitterBookTitlePage;
    frames: string[];
    onChanged?: (result: SkitterBookTitlePage) => void;
}

export const SkitterBookCoverPage: React.FC<SkitterBookCoverPageProps> = ({ isEnabled, data, frames, onChanged }) => {

    const loaderRef = useRef<{ triggerClick: () => void } | null>(null);
    const [showImageSelectorModal, setShowImageSelectorModal] = useState(false);
    const [coverImageOrientation, setCoverImageOrientation] = useState("landscape");
    const [selectedRawCoverImage, setSelectedRawCoverImage] = useState<string | null>(null);
    const [closeModalSignal, setCloseModalSignal] = useState(0);

    return (
        <div className="skitterbook-edit-cover-container">
            <div className="skitterbook-edit-cover-text">
                <div className="edit-title-container">
                    <TextInput
                        value={data.title}
                        isEnabled={isEnabled}
                        onSave={async (value) => {
                            const updatedResult = {
                                ...data,
                                title: value
                            }

                            onChanged?.(updatedResult);
                        }}
                    />
                </div>
                <div className="edit-subtitle-container">
                    <TextInput
                        value={data.subTitle}
                        isEnabled={isEnabled}
                        onSave={async (value) => {
                            const updatedResult = {
                                ...data,
                                subTitle: value
                            }

                            onChanged?.(updatedResult);
                        }}
                    />
                </div>
            </div>
            <div className={`edit-cover-image-container ${coverImageOrientation}`}>
                {data.titleImageBytes && (
                    <SkitterBookImageCropper
                        cropImage={data.titleImageBytes}
                        cropPosX={data.titleImageViewport.x}
                        cropPosY={data.titleImageViewport.y}
                        cropWidth={data.titleImageViewport.width}
                        cropHeight={data.titleImageViewport.height}
                        onCropChange={() => { }}
                        onCropChangeEnd={(cropOffset) => {
                            const updatedTitleImageViewport = {
                                ...data.titleImageViewport,
                                x: cropOffset.x,
                                y: cropOffset.y
                            }

                            const updatedResult = {
                                ...data,
                                titleImageViewport: updatedTitleImageViewport
                            }

                            onChanged?.(updatedResult);
                        }}>
                    </SkitterBookImageCropper>
                )}
                <div className="skitterbook-edit-controls">
                    <div className="edit-cover-image-buttons">
                        <ModalButton modalTitle={`Select your cover image from the clip's' frames`}
                            externalCloseSignal={closeModalSignal}
                            IconComponent={<ImageLibraryLoad />}>
                            <SkitterBookFrameSelection frames={frames}
                                onClick={frame => {
                                    const updatedResult = {
                                        ...data,
                                        titleImageBytes: frame
                                    }
                                    onChanged?.(updatedResult);
                                    setCloseModalSignal(Date.now());
                                }} />
                        </ModalButton>
                        <GlyphButton ariaLabel="coverImageDeviceLoad"
                            tooltipText="Select the cover image from your device"
                            IconComponent={<ImageDeviceLoad />}
                            disabled={!isEnabled}
                            onClick={() => loaderRef.current?.triggerClick()} />
                    </div>
                </div>
            </div>

            <ImageLoader
                refProp={loaderRef}
                onImageSelected={(base64ImageString) => {
                    if (isEnabled) {
                        setSelectedRawCoverImage(base64ImageString);
                    }
                }} />
        </div>
    );
};

export default SkitterBookCoverPage;
